import React, { useEffect, useMemo } from 'react';
import { Radio } from '@duxton-web/ui';

import { VariableType } from '@/constants';
import { useVariableContext } from '@/features/variable/contexts/variable-context';
import Enum from '@/features/variable/settings/validation/enum';
import NumberRange from '@/features/variable/settings/validation/number-range';
import StringRange from '@/features/variable/settings/validation/string-range';

export enum RangeMultipleValidationType {
  RANGE = 'Range',
  MULTIPLE = 'Multiple',
}

export const isRangeSupportedForVariableType = (type?: string) => {
  return [
    VariableType.STRING,
    VariableType.FILE,
    VariableType.INT64,
    VariableType.FLOAT64,
  ].includes(type as VariableType);
};

export const getRangeMultipleOptionsForVariableType = (type?: string) => {
  if (!isRangeSupportedForVariableType(type)) {
    return [
      {
        label: RangeMultipleValidationType.MULTIPLE,
        value: RangeMultipleValidationType.MULTIPLE,
      },
    ];
  }

  return [
    {
      label: RangeMultipleValidationType.RANGE,
      value: RangeMultipleValidationType.RANGE,
    },
    {
      label: RangeMultipleValidationType.MULTIPLE,
      value: RangeMultipleValidationType.MULTIPLE,
    },
  ];
};

interface RangeMultipleContentProps {
  value: RangeMultipleValidationType;
  onChange: (value: RangeMultipleValidationType) => void;
  disabled?: boolean;
  showSelector?: boolean;
}

const RangeMultipleContent = ({
  value,
  onChange,
  disabled,
  showSelector = true,
}: RangeMultipleContentProps) => {
  const { variable } = useVariableContext();
  const options = useMemo(
    () => getRangeMultipleOptionsForVariableType(variable?.type),
    [variable?.type],
  );

  useEffect(() => {
    const hasMatchingOption = options.some((option) => option.value === value);
    if (!hasMatchingOption && options[0]?.value) {
      onChange(options[0].value as RangeMultipleValidationType);
    }
  }, [onChange, options, value]);

  const renderRangeContent = () => {
    switch (variable?.type) {
      case VariableType.STRING:
      case VariableType.FILE:
        return <StringRange />;
      case VariableType.INT64:
      case VariableType.FLOAT64:
        return <NumberRange />;
      default:
        return <Enum />;
    }
  };

  return (
    <>
      {showSelector && (
        <Radio.Group
          optionType="button"
          disabled={disabled}
          options={options}
          value={value}
          onChange={(e) =>
            onChange(e.target.value as RangeMultipleValidationType)
          }
        />
      )}
      {value === RangeMultipleValidationType.RANGE ? (
        renderRangeContent()
      ) : (
        <Enum />
      )}
    </>
  );
};

export default RangeMultipleContent;
